import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { replaceCustomizationFields } from '../../api/business/endpoints';
import type { CustomizationField, CustomizationFieldInput, ProductDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import {
  keptFields,
  MAX_CUSTOMIZATION_FIELDS,
  movedField,
  withField,
  withoutField,
} from '../../components/customization';
import { ErrorNotice } from '../../components/ErrorNotice';
import { useContentLanguages } from '../../components/useContentLanguages';
import { useLanguage } from '../../i18n/LanguageContext';

import { CustomizationFieldDialog } from './CustomizationFieldDialog';

type ProductCustomizationSectionProps = {
  companyId: string;
  product: ProductDetail;
  canManage: boolean;
};

/**
 * What a customer fills in when ordering the product in a shop (R0030 WP-11) — a text or a file, required or not, a
 * label per language. Every change saves the whole list; a field keeps its id, so what customers entered stays
 * attached to it. The values themselves arrive with orders later.
 */
export const ProductCustomizationSection = ({
  companyId,
  product,
  canManage,
}: ProductCustomizationSectionProps) => {
  const { t } = useLanguage();
  const { languages } = useContentLanguages();
  const [editing, setEditing] = useState<{ index: number | null; field: CustomizationField | null } | null>(
    null,
  );
  const fields = product.customizationFields;
  const kept = keptFields(fields);
  const save = useAppMutation(
    (next: Array<CustomizationFieldInput>) => replaceCustomizationFields(companyId, product.id, next),
    ['product'],
    () => setEditing(null),
  );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
      >
        <Typography variant="h6">{t('customization.title')}</Typography>
        {canManage ? (
          <Button
            startIcon={<AddIcon />}
            disabled={fields.length >= MAX_CUSTOMIZATION_FIELDS || save.isPending}
            onClick={() => setEditing({ index: null, field: null })}
          >
            {t('customization.add')}
          </Button>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
        {t('customization.hint')}
      </Typography>
      {editing === null ? <ErrorNotice error={save.error} /> : null}
      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          {t('customization.empty')}
        </Typography>
      ) : (
        <List dense disablePadding>
          {fields.map((field, index) => (
            <ListItem
              key={field.id}
              disableGutters
              secondaryAction={
                canManage ? (
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t('customization.edit')}>
                      <IconButton
                        size="small"
                        aria-label={t('customization.edit')}
                        onClick={() => setEditing({ index, field })}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('customization.moveUp')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('customization.moveUp')}
                          disabled={index === 0 || save.isPending}
                          onClick={() => save.mutate(movedField(kept, index, -1))}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('customization.moveDown')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('customization.moveDown')}
                          disabled={index === fields.length - 1 || save.isPending}
                          onClick={() => save.mutate(movedField(kept, index, 1))}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t('customization.remove')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('customization.remove')}
                          disabled={save.isPending}
                          onClick={() => save.mutate(withoutField(kept, index))}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : undefined
              }
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <span>{field.labels[languages[0]] ?? t('common.none')}</span>
                    <Chip size="small" variant="outlined" label={t(`customization.type.${field.type}`)} />
                    {field.required ? (
                      <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={t('customization.requiredChip')}
                      />
                    ) : null}
                  </Stack>
                }
                secondary={languages
                  .slice(1)
                  .map((code) => field.labels[code])
                  .filter(Boolean)
                  .join(' · ')}
              />
            </ListItem>
          ))}
        </List>
      )}
      {editing ? (
        <CustomizationFieldDialog
          field={editing.field}
          isBusy={save.isPending}
          error={save.error}
          onClose={() => setEditing(null)}
          onSave={(field) => save.mutate(withField(kept, editing.index, field))}
        />
      ) : null}
    </Paper>
  );
};
